import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuth } from '../context/AuthContext'
import {
  FaStar,
  FaMapMarkerAlt,
  FaClock,
  FaUsers,
  FaCalendar,
  FaCar,
  FaUserTie,
  FaHotel,
  FaDollarSign,
  FaCheck,
  FaMinus,
  FaPlus,
  FaTasks,
  FaTicketAlt,
  FaTrash,
} from 'react-icons/fa'
import Loader from '../components/Loader'
import GuestDetailsForm from '../components/GuestDetailsForm'
import PriceBreakup from '../components/PriceBreakup'
import tourService from '../services/tourService'
import serviceService from '../services/serviceService'
import bookingService from '../services/bookingService'
import discountService from '../services/discountService'
import { formatPrice } from '../utils/helpers'
import { buildPriceBreakup } from '../utils/bookingCalculations'
import SEO from '../utils/SEO'
import { trackBeginCheckout, trackPurchase } from '../utils/analytics'
import { validateGuestDetails } from '../utils/validators'
import Button from '../components/ui/Button'
import PolicyDisplay from '../components/PolicyDisplay'
import cancellationPolicyService from '../services/cancellationPolicyService'

const TourBooking = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [tour, setTour] = useState(null)
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [cancellationPolicy, setCancellationPolicy] = useState(null)
  const [termsPolicy, setTermsPolicy] = useState(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      cancellationPolicyService.getActive('cancellation').catch(() => null),
      cancellationPolicyService.getActive('terms_and_conditions').catch(() => null),
    ]).then(([c, t]) => {
      if (cancelled) return
      setCancellationPolicy(c?.policy || null)
      setTermsPolicy(t?.policy || null)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Booking form state
  const [bookingData, setBookingData] = useState({
    start_date: '',
    end_date: '',
    num_adults: 1,
    num_children: 0,
    group_type: 'individual',
    special_requests: '',
    // User details
    name: user?.name || '',
    email: user?.email || '',
    mobile: user?.mobile_number || '',
  })

  // Selected services state
  const [selectedServices, setSelectedServices] = useState({
    cars: [],
    guides: [],
    hotels: [],
    activities: [],
  })

  // Guest details state
  const [guests, setGuests] = useState([])
  const [guestErrors, setGuestErrors] = useState({})

  // Coupon/discount state
  const [couponCode, setCouponCode] = useState('')

  // Per-service remarks state
  const [serviceRemarks, setServiceRemarks] = useState({})

  const updateServiceRemark = (type, serviceId, value) => {
    setServiceRemarks((prev) => ({ ...prev, [`${type}_${serviceId}`]: value }))
  }
  const [appliedDiscount, setAppliedDiscount] = useState(null)
  const [validatingCoupon, setValidatingCoupon] = useState(false)

  useEffect(() => {
    loadData()
    loadPreviousSelections()
  }, [id])

  // Auto-select pre-selected service from Services page
  useEffect(() => {
    if (services.cars || services.guides || services.hotels || services.activities) {
      const preSelected = localStorage.getItem('preSelectedService')
      if (preSelected) {
        try {
          const serviceInfo = JSON.parse(preSelected)
          const serviceType =
            serviceInfo.type === 'car'
              ? 'cars'
              : serviceInfo.type === 'guide'
                ? 'guides'
                : serviceInfo.type === 'activity'
                  ? 'activities'
                  : serviceInfo.type === 'hotel' || serviceInfo.type === 'homestay'
                    ? 'hotels'
                    : null

          if (serviceType && services[serviceType]) {
            const service = services[serviceType].find((s) => s.id === serviceInfo.id)
            if (service) {
              // Auto-select the service
              toggleService(serviceType, service.id)
              // Clear the pre-selected service from localStorage
              localStorage.removeItem('preSelectedService')
              // Scroll to the service section
              setTimeout(() => {
                const element = document.getElementById(`service-${serviceType}`)
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
              }, 500)
            }
          }
        } catch (error) {
          console.error('Error auto-selecting service', error)
        }
      }
    }
  }, [services])

  useEffect(() => {
    if (user) {
      setBookingData((prev) => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        mobile: user.mobile_number || '',
      }))
    }
  }, [user])

  // Sync guests array when num_adults or num_children changes
  useEffect(() => {
    const numAdults = Math.max(1, Number.parseInt(bookingData.num_adults) || 1)
    const numChildren = Math.max(0, Number.parseInt(bookingData.num_children) || 0)
    const total = numAdults + numChildren

    setGuests((prev) => {
      const newGuests = []
      // Adults
      for (let i = 0; i < numAdults; i++) {
        if (prev[i] && !prev[i].is_child) {
          newGuests.push(prev[i])
        } else {
          newGuests.push({
            full_name: i === 0 ? user?.name || '' : '',
            age: '',
            gender: '',
            id_type: 'none',
            id_number: '',
            is_child: false,
          })
        }
      }
      // Children
      const prevChildren = prev.filter((g) => g.is_child)
      for (let i = 0; i < numChildren; i++) {
        if (prevChildren[i]) {
          newGuests.push(prevChildren[i])
        } else {
          newGuests.push({
            full_name: '',
            age: '',
            gender: '',
            id_type: 'none',
            id_number: '',
            is_child: true,
          })
        }
      }
      return newGuests.length === total ? newGuests : newGuests.slice(0, total)
    })
    setGuestErrors({})
  }, [bookingData.num_adults, bookingData.num_children, user])

  const loadData = async () => {
    try {
      setLoading(true)
      const [tourData, servicesData] = await Promise.all([
        tourService.getTourById(id),
        serviceService.getAllServices(),
      ])

      setTour(tourData.tour)
      trackBeginCheckout(
        {
          id: tourData.tour.id,
          name: tourData.tour.title || tourData.tour.name,
          price: tourData.tour.price,
          category: 'tour',
        },
        tourData.tour.price,
      )

      // Filter services by type
      const cars = servicesData.services.filter((s) => s.type === 'car' && s.is_active)
      const guides = servicesData.services.filter((s) => s.type === 'guide' && s.is_active)
      const hotels = servicesData.services.filter(
        (s) => (s.type === 'hotel' || s.type === 'homestay') && s.is_active,
      )
      const activities = servicesData.services.filter((s) => s.type === 'activity' && s.is_active)

      setServices({ cars, guides, hotels, activities })
      setLoading(false)
    } catch (error) {
      toast.error('Failed to load booking details')
      setLoading(false)
    }
  }

  const loadPreviousSelections = () => {
    // Load from localStorage if exists
    const saved = localStorage.getItem(`booking_${id}`)
    if (saved) {
      try {
        const data = JSON.parse(saved)
        setSelectedServices(
          data.selectedServices || { cars: [], guides: [], hotels: [], activities: [] },
        )
        setBookingData((prev) => ({
          ...prev,
          ...data.bookingData,
          name: user?.name || data.bookingData.name,
          email: user?.email || data.bookingData.email,
          mobile: user?.mobile_number || data.bookingData.mobile,
        }))
      } catch (error) {
        console.error('Error loading previous selections', error)
      }
    }

    // Check for pre-selected service from Services page
    const preSelected = localStorage.getItem('preSelectedService')
    if (preSelected) {
      try {
        const serviceInfo = JSON.parse(preSelected)
        toast.success(`${serviceInfo.name} will be auto-selected for you!`, {
          position: 'top-center',
          autoClose: 3000,
        })
        // Note: Actual selection will happen in useEffect after services are loaded
      } catch (error) {
        console.error('Error parsing pre-selected service', error)
      }
    }
  }

  const saveSelections = () => {
    // Save to localStorage
    localStorage.setItem(
      `booking_${id}`,
      JSON.stringify({
        selectedServices,
        bookingData,
      }),
    )
  }

  useEffect(() => {
    saveSelections()
  }, [selectedServices, bookingData])

  const toggleService = (type, serviceId) => {
    setSelectedServices((prev) => {
      const currentList = prev[type] || []
      const exists = currentList.find((s) => s.id === serviceId)

      if (exists) {
        // Remove service
        return {
          ...prev,
          [type]: currentList.filter((s) => s.id !== serviceId),
        }
      } else {
        // Add service with quantity
        const service = services[type].find((s) => s.id === serviceId)
        return {
          ...prev,
          [type]: [...currentList, { ...service, quantity: 1 }],
        }
      }
    })
  }

  const updateServiceQuantity = (type, serviceId, delta) => {
    setSelectedServices((prev) => {
      const currentList = prev[type] || []
      return {
        ...prev,
        [type]: currentList.map((s) =>
          s.id === serviceId ? { ...s, quantity: Math.max(1, s.quantity + delta) } : s,
        ),
      }
    })
  }

  const calculateDays = () => {
    if (!bookingData.start_date || !bookingData.end_date) return 0
    const start = new Date(bookingData.start_date)
    const end = new Date(bookingData.end_date)
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays || 1
  }

  const calculateServiceCost = (service) => {
    const days = Math.max(1, calculateDays())
    const numAdults = parseInt(bookingData.num_adults) || 1
    const numChildren = parseInt(bookingData.num_children) || 0
    const basePrice = parseFloat(service.base_price) || 0
    let cost = basePrice * (service.quantity || 1)
    if (service.price_unit === 'per_day') {
      cost *= days
    } else if (service.price_unit === 'per_person') {
      cost *= numAdults + numChildren
    }
    return cost
  }

  const calculatePrice = () => {
    if (!tour) return 0
    const numAdults = parseInt(bookingData.num_adults) || 1
    const numChildren = parseInt(bookingData.num_children) || 0
    const basePrice = parseFloat(tour.base_price) || 0

    let total = basePrice * (numAdults + numChildren * 0.5)

    // Add service costs
    Object.values(selectedServices).forEach((serviceList) => {
      serviceList.forEach((service) => {
        total += calculateServiceCost(service)
      })
    })

    return total
  }

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code')
      return
    }

    const subtotal = calculatePrice()
    if (subtotal === 0) {
      toast.error('Please complete your booking details first')
      return
    }

    try {
      setValidatingCoupon(true)
      const response = await discountService.validateDiscount(couponCode, subtotal)

      if (response.success) {
        setAppliedDiscount(response.discount)
        toast.success(`Coupon applied! You saved ₹${response.discount.discount_amount}`)
      } else {
        toast.error(response.message || 'Invalid coupon code')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid or expired coupon code'
      toast.error(errorMessage)
      setAppliedDiscount(null)
    } finally {
      setValidatingCoupon(false)
    }
  }

  const handleRemoveCoupon = () => {
    setCouponCode('')
    setAppliedDiscount(null)
    toast.info('Coupon removed')
  }

  const getFinalPrice = () => {
    const subtotal = calculatePrice()
    if (!appliedDiscount) return subtotal

    let discountAmount = 0
    if (appliedDiscount.discount_type === 'percentage') {
      discountAmount = subtotal * (Number.parseFloat(appliedDiscount.discount_value) / 100)
      if (
        appliedDiscount.max_discount_amount &&
        discountAmount > Number.parseFloat(appliedDiscount.max_discount_amount)
      ) {
        discountAmount = Number.parseFloat(appliedDiscount.max_discount_amount)
      }
    } else {
      discountAmount = Number.parseFloat(appliedDiscount.discount_value)
    }
    return Math.max(0, subtotal - discountAmount)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setBookingData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!user) {
      toast.error('Please login to book a tour')
      navigate('/login', { state: { from: `/tours/${id}/book` } })
      return
    }

    if (!bookingData.start_date || !bookingData.end_date) {
      toast.error('Please select travel dates')
      return
    }

    // Validate guest details
    const { errors, hasErrors } = validateGuestDetails(guests)
    if (hasErrors) {
      setGuestErrors(errors)
      toast.error('Please fill in all required guest details')
      return
    }
    setGuestErrors({})

    setSubmitting(true)
    try {
      const subtotal = calculatePrice()
      const allServices = [
        ...selectedServices.cars,
        ...selectedServices.guides,
        ...selectedServices.hotels,
        ...selectedServices.activities,
      ]

      const priceBreakup = buildPriceBreakup({
        tour,
        numAdults: parseInt(bookingData.num_adults) || 1,
        numChildren: parseInt(bookingData.num_children) || 0,
        services: allServices,
        calcServiceCost: calculateServiceCost,
        discount: appliedDiscount,
        subtotal,
        total: getFinalPrice(),
      })

      const bookingPayload = {
        tour_id: parseInt(id),
        start_date: bookingData.start_date,
        end_date: bookingData.end_date,
        num_adults: parseInt(bookingData.num_adults),
        num_children: parseInt(bookingData.num_children),
        total_amount: getFinalPrice(),
        discount_code: appliedDiscount ? couponCode : null,
        discount_amount: appliedDiscount ? Number.parseFloat(appliedDiscount.discount_amount) : 0,
        group_type: bookingData.group_type,
        special_requests: bookingData.special_requests,
        services: [
          ...selectedServices.cars.map((s) => ({ service_id: s.id, quantity: s.quantity })),
          ...selectedServices.guides.map((s) => ({ service_id: s.id, quantity: s.quantity })),
          ...selectedServices.hotels.map((s) => ({ service_id: s.id, quantity: s.quantity })),
          ...selectedServices.activities.map((s) => ({ service_id: s.id, quantity: s.quantity })),
        ],
        guests: guests.map((g) => ({
          full_name: g.full_name.trim(),
          age: Number.parseInt(g.age),
          gender: g.gender,
          id_type: g.id_type || 'none',
          id_number: g.id_type && g.id_type !== 'none' ? g.id_number?.trim() : null,
          is_child: !!g.is_child,
        })),
        price_breakup: priceBreakup,
        service_remarks: (() => {
          const remarks = {}
          for (const car of selectedServices.cars) {
            if (serviceRemarks[`car_${car.id}`]) remarks.car = serviceRemarks[`car_${car.id}`]
          }
          for (const guide of selectedServices.guides) {
            if (serviceRemarks[`guide_${guide.id}`])
              remarks.guide = serviceRemarks[`guide_${guide.id}`]
          }
          for (const hotel of selectedServices.hotels) {
            if (serviceRemarks[`hotel_${hotel.id}`])
              remarks.hotel = serviceRemarks[`hotel_${hotel.id}`]
          }
          const activityRemarks = selectedServices.activities
            .filter((a) => serviceRemarks[`activity_${a.id}`])
            .map((a) => ({ service_id: a.id, remarks: serviceRemarks[`activity_${a.id}`] }))
          if (activityRemarks.length > 0) remarks.activities = activityRemarks
          return Object.keys(remarks).length > 0 ? remarks : null
        })(),
      }

      const result = await bookingService.createBooking(bookingPayload)
      trackPurchase({
        id: result.booking?.id || result.id,
        item_id: id,
        item_name: tour.title || tour.name,
        total_price: result.booking?.total_price || tour.price,
        category: 'tour',
      })

      // Clear saved selections
      localStorage.removeItem(`booking_${id}`)

      toast.success('Booking created successfully!')
      navigate('/bookings')
    } catch (error) {
      toast.error(error.message || 'Failed to create booking')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size={80} />
      </div>
    )
  }

  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Tour Not Found</h2>
          <Button onClick={() => navigate('/tours')}>Back to Tours</Button>
        </div>
      </div>
    )
  }

  const totalPrice = calculatePrice()

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <SEO title="Book Tour" noindex />
      <div className="container mx-auto px-4 max-w-7xl">
        <button
          onClick={() => navigate(`/tours/${id}`)}
          className="mb-6 text-blue-600 hover:text-blue-700 flex items-center gap-2"
        >
          ← Back to Tour Details
        </button>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2">
            {/* Tour Summary */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{tour.title}</h1>
              <div className="flex items-center gap-4 text-gray-600 text-sm">
                <span className="flex items-center gap-1">
                  <FaMapMarkerAlt className="text-blue-600" />
                  {tour.destination}
                </span>
                <span className="flex items-center gap-1">
                  <FaClock />
                  {tour.duration} Days
                </span>
                {tour.average_rating > 0 && (
                  <span className="flex items-center gap-1">
                    <FaStar className="text-yellow-400" />
                    {tour.average_rating} ({tour.total_reviews} reviews)
                  </span>
                )}
              </div>
            </div>

            {/* Booking Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Travel Dates */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <FaCalendar className="text-blue-600" />
                  Travel Dates
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      value={bookingData.start_date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      name="end_date"
                      value={bookingData.end_date}
                      onChange={handleInputChange}
                      min={bookingData.start_date || new Date().toISOString().split('T')[0]}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Travelers */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <FaUsers className="text-blue-600" />
                  Number of Travelers
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Adults *</label>
                    <input
                      type="number"
                      name="num_adults"
                      value={bookingData.num_adults}
                      onChange={handleInputChange}
                      min="1"
                      max={tour.max_group_size}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Children</label>
                    <input
                      type="number"
                      name="num_children"
                      value={bookingData.num_children}
                      onChange={handleInputChange}
                      min="0"
                      max={tour.max_group_size}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Group Type
                    </label>
                    <select
                      name="group_type"
                      value={bookingData.group_type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="individual">Individual</option>
                      <option value="group">Group</option>
                      <option value="corporate">Corporate</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Guest Details */}
              {guests.length > 0 && (
                <GuestDetailsForm guests={guests} onChange={setGuests} errors={guestErrors} />
              )}

              {/* Add Services - Cars */}
              {services.cars && services.cars.length > 0 && (
                <div id="service-cars" className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <FaCar className="text-blue-600" />
                    Add Car Service (Optional)
                  </h3>
                  <div className="space-y-3">
                    {services.cars.map((car) => {
                      const selected = selectedServices.cars?.find((s) => s.id === car.id)
                      return (
                        <div
                          key={car.id}
                          className={`border rounded-lg p-4 cursor-pointer transition ${
                            selected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-300 hover:border-blue-300'
                          }`}
                          onClick={() => toggleService('cars', car.id)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{car.name}</h4>
                              <p className="text-sm text-gray-600">{car.description}</p>
                              <div className="mt-2 flex items-center gap-4 text-sm">
                                <span className="text-blue-600 font-semibold">
                                  {formatPrice(car.base_price)}/{car.price_unit?.replace('_', ' ')}
                                </span>
                                {car.capacity && (
                                  <span className="text-gray-500">Capacity: {car.capacity}</span>
                                )}
                              </div>
                            </div>
                            {selected && (
                              <div
                                className="flex items-center gap-2 ml-4"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={() => updateServiceQuantity('cars', car.id, -1)}
                                  className="p-1 bg-gray-200 rounded hover:bg-gray-300"
                                >
                                  <FaMinus size={12} />
                                </button>
                                <span className="font-semibold w-8 text-center">
                                  {selected.quantity}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => updateServiceQuantity('cars', car.id, 1)}
                                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                  <FaPlus size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                          {selected && (
                            <input
                              type="text"
                              placeholder="Add a remark for this car (e.g., automatic transmission)"
                              value={serviceRemarks[`car_${car.id}`] || ''}
                              onChange={(e) => updateServiceRemark('car', car.id, e.target.value)}
                              maxLength={500}
                              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Add Services - Guides */}
              {services.guides && services.guides.length > 0 && (
                <div id="service-guides" className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <FaUserTie className="text-blue-600" />
                    Add Tour Guide (Optional)
                  </h3>
                  <div className="space-y-3">
                    {services.guides.map((guide) => {
                      const selected = selectedServices.guides?.find((s) => s.id === guide.id)
                      return (
                        <div
                          key={guide.id}
                          className={`border rounded-lg p-4 cursor-pointer transition ${
                            selected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-300 hover:border-blue-300'
                          }`}
                          onClick={() => toggleService('guides', guide.id)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{guide.name}</h4>
                              <p className="text-sm text-gray-600">{guide.description}</p>
                              <div className="mt-2 flex items-center gap-4 text-sm">
                                <span className="text-blue-600 font-semibold">
                                  {formatPrice(guide.base_price)}/
                                  {guide.price_unit?.replace('_', ' ')}
                                </span>
                                {guide.experience_years > 0 && (
                                  <span className="text-gray-500">
                                    {guide.experience_years} years experience
                                  </span>
                                )}
                                {guide.average_rating > 0 && (
                                  <span className="flex items-center gap-1 text-yellow-600">
                                    <FaStar size={12} />
                                    {guide.average_rating}
                                  </span>
                                )}
                              </div>
                            </div>
                            {selected && (
                              <div
                                className="flex items-center gap-2 ml-4"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={() => updateServiceQuantity('guides', guide.id, -1)}
                                  className="p-1 bg-gray-200 rounded hover:bg-gray-300"
                                >
                                  <FaMinus size={12} />
                                </button>
                                <span className="font-semibold w-8 text-center">
                                  {selected.quantity}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => updateServiceQuantity('guides', guide.id, 1)}
                                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                  <FaPlus size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                          {selected && (
                            <input
                              type="text"
                              placeholder="Add a remark for this guide (e.g., Hindi-speaking preferred)"
                              value={serviceRemarks[`guide_${guide.id}`] || ''}
                              onChange={(e) =>
                                updateServiceRemark('guide', guide.id, e.target.value)
                              }
                              maxLength={500}
                              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Add Services - Hotels */}
              {services.hotels && services.hotels.length > 0 && (
                <div id="service-hotels" className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <FaHotel className="text-blue-600" />
                    Add Accommodation (Optional)
                  </h3>
                  <div className="space-y-3">
                    {services.hotels.map((hotel) => {
                      const selected = selectedServices.hotels?.find((s) => s.id === hotel.id)
                      return (
                        <div
                          key={hotel.id}
                          className={`border rounded-lg p-4 cursor-pointer transition ${
                            selected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-300 hover:border-blue-300'
                          }`}
                          onClick={() => toggleService('hotels', hotel.id)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{hotel.name}</h4>
                              <p className="text-sm text-gray-600">{hotel.description}</p>
                              <div className="mt-2 text-blue-600 font-semibold text-sm">
                                {formatPrice(hotel.base_price)}/
                                {hotel.price_unit?.replace('_', ' ')}
                              </div>
                            </div>
                            {selected && <FaCheck className="text-blue-600 mt-1" />}
                          </div>
                          {selected && (
                            <input
                              type="text"
                              placeholder="Add a remark for this hotel (e.g., vegetarian meals, ground floor)"
                              value={serviceRemarks[`hotel_${hotel.id}`] || ''}
                              onChange={(e) =>
                                updateServiceRemark('hotel', hotel.id, e.target.value)
                              }
                              maxLength={500}
                              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Add Services - Adventure Activities */}
              {services.activities && services.activities.length > 0 && (
                <div id="service-activities" className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <FaTasks className="text-blue-600" />
                    Add Adventure Activities (Optional)
                  </h3>
                  <div className="space-y-3">
                    {services.activities.map((activity) => {
                      const selected = selectedServices.activities?.find(
                        (s) => s.id === activity.id,
                      )
                      return (
                        <div
                          key={activity.id}
                          className={`border rounded-lg p-4 cursor-pointer transition ${
                            selected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-300 hover:border-blue-300'
                          }`}
                          onClick={() => toggleService('activities', activity.id)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{activity.name}</h4>
                              <p className="text-sm text-gray-600">{activity.description}</p>
                              <div className="mt-2 flex items-center gap-4 text-sm">
                                <span className="text-blue-600 font-semibold">
                                  {formatPrice(activity.base_price)}/
                                  {activity.price_unit?.replace('_', ' ')}
                                </span>
                                {activity.capacity && (
                                  <span className="text-gray-500">
                                    Max: {activity.capacity} persons
                                  </span>
                                )}
                              </div>
                            </div>
                            {selected && (
                              <div
                                className="flex items-center gap-2 ml-4"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateServiceQuantity('activities', activity.id, -1)
                                  }
                                  className="p-1 bg-gray-200 rounded hover:bg-gray-300"
                                >
                                  <FaMinus size={12} />
                                </button>
                                <span className="font-semibold w-8 text-center">
                                  {selected.quantity}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateServiceQuantity('activities', activity.id, 1)
                                  }
                                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                  <FaPlus size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                          {selected && (
                            <input
                              type="text"
                              placeholder="Add a remark for this activity (e.g., beginner level)"
                              value={serviceRemarks[`activity_${activity.id}`] || ''}
                              onChange={(e) =>
                                updateServiceRemark('activity', activity.id, e.target.value)
                              }
                              maxLength={500}
                              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Special Requests */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold mb-4">Special Requests (Optional)</h3>
                <textarea
                  name="special_requests"
                  value={bookingData.special_requests}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Any special requirements or preferences..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </form>
          </div>

          {/* Booking Summary Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-lg sticky top-8">
              <h3 className="text-xl font-bold mb-4">Booking Summary</h3>

              <div className="mb-6 pb-6 border-b">
                <PriceBreakup
                  breakup={(() => {
                    const numAdults = parseInt(bookingData.num_adults) || 1
                    const numChildren = parseInt(bookingData.num_children) || 0
                    const sub = calculatePrice()
                    const allSvcs = [
                      ...selectedServices.cars,
                      ...selectedServices.guides,
                      ...selectedServices.hotels,
                      ...selectedServices.activities,
                    ]
                    const b = {
                      tour: {
                        name: tour.title || tour.name,
                        base_price: parseFloat(tour.base_price) || 0,
                        adults: numAdults,
                        children: numChildren,
                        adult_total: (parseFloat(tour.base_price) || 0) * numAdults,
                        child_total: (parseFloat(tour.base_price) || 0) * 0.5 * numChildren,
                      },
                      services: allSvcs.map((s) => ({
                        name: s.name,
                        quantity: s.quantity || 1,
                        cost: calculateServiceCost(s),
                      })),
                      subtotal: sub,
                      total: getFinalPrice(),
                    }
                    if (appliedDiscount) {
                      b.discount = {
                        code: appliedDiscount.code,
                        type: appliedDiscount.discount_type,
                        value: Number.parseFloat(appliedDiscount.discount_value),
                        amount: Number.parseFloat(appliedDiscount.discount_amount),
                      }
                    }
                    return b
                  })()}
                />
              </div>

              {/* Coupon Code */}
              <div className="mb-4 pb-4 border-b">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                  <FaTicketAlt className="text-blue-500" size={12} />
                  Coupon Code
                </label>
                {appliedDiscount ? (
                  <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-700">
                        <FaCheck size={10} /> {appliedDiscount.code}
                      </span>
                      <button
                        onClick={handleRemoveCoupon}
                        className="flex h-7 w-7 items-center justify-center rounded-full text-red-500 transition hover:bg-red-50 hover:text-red-700"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                    <p className="text-sm text-green-600">{appliedDiscount.description}</p>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={validatingCoupon}
                    />
                    <Button
                      variant="secondary"
                      onClick={handleApplyCoupon}
                      disabled={validatingCoupon || !couponCode.trim() || totalPrice === 0}
                      className="whitespace-nowrap"
                    >
                      {validatingCoupon ? 'Checking...' : 'Apply'}
                    </Button>
                  </div>
                )}
              </div>

              {appliedDiscount && (
                <p className="text-sm text-green-600 mb-4">
                  You saved {formatPrice(Number.parseFloat(appliedDiscount.discount_amount))}!
                </p>
              )}

              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={!bookingData.start_date || !bookingData.end_date}
                loading={submitting}
                fullWidth
              >
                Confirm Booking
              </Button>

              <div className="mt-4 space-y-2">
                <PolicyDisplay policy={cancellationPolicy} title="Cancellation Policy" />
                <PolicyDisplay policy={termsPolicy} title="Terms & Conditions" />
              </div>

              <div className="mt-4 space-y-2 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <FaCheck className="text-green-500" />
                  <span>Free cancellation up to 48 hours</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaCheck className="text-green-500" />
                  <span>Instant confirmation</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaCheck className="text-green-500" />
                  <span>Secure payment</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TourBooking
